import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@commander/ui';
import { createContext, useContext, useState } from 'react';

interface ExpandableContextValue {
  title: string;
  canExpand: boolean;
  setExpanded: (v: boolean) => void;
}

const ExpandableContext = createContext<ExpandableContextValue | null>(null);

/**
 * Read the ExpandableBlock context. Returns `null` when not inside an
 * expandable wrapper, so callers can render alternate UI without crashing.
 * Components like footers or inline links use this to build custom expand
 * triggers beyond the default icon button.
 */
export function useExpandable(): ExpandableContextValue | null {
  return useContext(ExpandableContext);
}

/**
 * Expand icon button — renders only when an ExpandableBlock ancestor has
 * `expandedContent`. Cards place this inside their own header row so the
 * icon sits next to the title instead of floating as an absolute overlay.
 *
 * Safe to render unconditionally: if no ExpandableBlock wraps it, or the
 * wrapper has no expandedContent, this renders nothing.
 */
export function ExpandTrigger({ className }: { className?: string }) {
  const ctx = useContext(ExpandableContext);
  if (!ctx || !ctx.canExpand) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className={className ?? 'h-6 w-6 shrink-0 p-0'}
      aria-label={`Expand ${ctx.title}`}
      onClick={() => ctx.setExpanded(true)}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="15 3 21 3 21 9" />
        <polyline points="9 21 3 21 3 15" />
        <line x1="21" y1="3" x2="14" y2="10" />
        <line x1="3" y1="21" x2="10" y2="14" />
      </svg>
    </Button>
  );
}

interface ExpandableBlockProps {
  title: string;
  children: React.ReactNode;
  expandedContent?: React.ReactNode;
  dialogStyle?: React.CSSProperties;
}

export function ExpandableBlock({
  title,
  children,
  expandedContent,
  dialogStyle,
}: ExpandableBlockProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <ExpandableContext.Provider value={{ title, canExpand: !!expandedContent, setExpanded }}>
      {children}

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent
          // Inline styles are load-bearing — the shared DialogContent sets
          // `display: grid` and has no max-height, so we must override both
          // Radix Dialog auto-focuses the first focusable element on open,
          // which lands on the close (×) button and leaves a visible focus
          // ring over the corner. Suppress that so the user sees the card
          // content, not the close button highlighted.
          onOpenAutoFocus={(e) => e.preventDefault()}
          // here to force a bounded flex column. Leaving these as className
          // utilities risks tailwind-merge dedupe doing the wrong thing.
          style={
            {
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'calc(100dvh - 2rem)',
              overflow: 'hidden',
              minWidth: 'min(600px, 90vw)',
              width: 'fit-content',
              maxWidth: 'min(1100px, 90vw)',
              ...dialogStyle,
            } as Record<string, unknown>
          }
        >
          <DialogHeader style={{ flexShrink: 0 }}>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="sr-only">Expanded view of {title}</DialogDescription>
          </DialogHeader>
          <div
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'auto',
              overscrollBehavior: 'contain',
            }}
          >
            {expandedContent}
          </div>
        </DialogContent>
      </Dialog>
    </ExpandableContext.Provider>
  );
}
